import { Fragment, useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CircularProgress, Divider, Grid } from "@mui/material";
import axios from "axios";
import { LocaleContext } from "../../routing/LangRouter";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

//import constants
import BASE_URL from "../../constants/baseUrls";

//import utils
import { numberWithCommas } from "../../utils/utility";

//import general components
import { BaseButton } from "../../components/common/base-button/base-button";

//import images and icons
import { CloseCircle, InfoCircle, TickCircle } from "iconsax-react";

//import styles
import "./payment-result.scss";

function PaymentResult() {
    //general variables
    let [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { locale } = useContext(LocaleContext);
    const [loading, setLoading] = useState(true);
    const { t } = useTranslation();

    const pk = searchParams.get("pk");
    const wallet = searchParams.get("wallet");
    const [paymentResult, setPaymentResult] = useState({});
    const [errorFlag, setErrorFlag] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const { isAutomaticReserve } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};

    const getPaymentResult = async () => {
        try {
            setErrorMessage("");
            const response = await axios.post(
                `${BASE_URL}/api/v2/reserve/payment_result/`,
                {},
                {
                    params: {
                        pk,
                        wallet: wallet || null,
                    },
                },
            );
            if (response.status === 200 && isAutomaticReserve) {
                navigate(`/${locale}/flight-automatic-reserve/confirm`);
            }
            if (
                response?.data?.data.errors?.some(
                    err => err.Error.Id === "Err0107032" || err.Error.Id === "Err0123012" || err.Error.Id === "Err0144032",
                )
            ) {
                setErrorMessage(`رزرو تکراری: ${response?.data?.data.errors.map(err => err.Error.Message).join(", ")}`);
            } else setPaymentResult(response?.data?.data);
        } catch (error) {
            toast.error(t("Toast Messages.Payment Processing Issue"));
            setErrorFlag(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (pk) {
            getPaymentResult();
        }
    }, [pk]);

    const goToApp = () => {
        if (paymentResult?.confirm) {
            window.location.replace(`atr://atripa.app.ir/payment_success`);
        } else {
            window.location.replace(`atr://atripa.app.ir/payment_fail`);
        }
    };

    // Object containing navigation handler functions for different routes
    const handleNavigation = {
        home: () => navigate(`/${locale}`),
        userPanel: () => navigate(`/${locale}/user-panel/orders`),
        repayment: () => navigate(`/${locale}/confirm-information`),
    };

    const confirmClass = paymentResult?.confirm ? "confirm" : "error";
    const renderBooleanIcon = condition => (condition ? <TickCircle color="var(--primary-color)" /> : <CloseCircle color="var(--red-color)" />);

    return (
        <Grid className="payment-result" container justifyContent="center" alignItems="center">
            {loading ? (
                <Grid className={`payment-result__card progress`} container item xs={11} md={4} rowGap={2} direction="column" alignItems="center">
                    <h2 className="payment-result__progress">
                        {t("Messages.Reservation in Progress")} <CircularProgress size={30} sx={{ color: "var(--primary-color)" }} />
                    </h2>
                    <span className="payment-result__progress-description">{t("Messages.Do Not Leave Page")}</span>
                </Grid>
            ) : errorMessage ? (
                <Grid className={`payment-result__card ${confirmClass}`} container item xs={11} md={4} rowGap={3}>
                    <span className={`payment-result__card-title ${confirmClass}`}>{errorMessage}</span>
                </Grid>
            ) : (
                <Grid className={`payment-result__card ${confirmClass}`} container item xs={11} md={4} rowGap={3}>
                    <Grid container item xs={12} direction="column" alignItems="center">
                        {renderBooleanIcon(paymentResult?.confirm)}
                        <span className={`payment-result__card-title ${confirmClass}`}>
                            {paymentResult?.status || t("Messages.Transaction Error")}
                        </span>
                    </Grid>

                    <Grid container item xs={12} rowGap={2}>
                        {[
                            { label: t("trackingCode"), value: paymentResult?.reference_number ?? "-" },
                            { label: t("amount"), value: `${numberWithCommas(paymentResult?.amount || 0)} ${t("currency")}` },
                            { label: t("General.Payment Gateway"), value: paymentResult?.terminal },
                        ].map((item, index) => (
                            <Fragment key={index}>
                                <Grid container item xs={12} justifyContent="space-between">
                                    <span>{item.label}</span>
                                    <span>{item.value}</span>
                                </Grid>
                                {index < 2 && <Divider width="100%" />}
                            </Fragment>
                        ))}
                    </Grid>

                    {paymentResult?.fare_type !== 4 && paymentResult?.confirm && (
                        <Grid className="payment-result__fare" container item xs={12} columnGap={1} justifyContent="center" alignItems="center">
                            <InfoCircle color="var(--mustard-color)" />
                            <span>{t("Messages.Initial Reservation Completed")}</span>
                            <span>{t("Messages.Final Reservation Tracking")}</span>
                        </Grid>
                    )}

                    <Grid container item xs={12} justifyContent="space-evenly" rowGap={2}>
                        {paymentResult?.mobile ? (
                            <Grid container item xs={8} md={3}>
                                <BaseButton
                                    text={t("General.Return to Application")}
                                    color="primary"
                                    radius="radius-10"
                                    size="medium"
                                    handleClick={goToApp}
                                />
                            </Grid>
                        ) : (
                            <>
                                <Grid container item xs={8} md={3}>
                                    <BaseButton
                                        text={paymentResult?.confirm || errorFlag ? t("userAccount") : t("General.Retry Payment")}
                                        color="primary"
                                        radius="radius-10"
                                        size="medium"
                                        handleClick={paymentResult?.confirm || errorFlag ? handleNavigation.userPanel : handleNavigation.repayment}
                                    />
                                </Grid>
                                <Grid container item xs={8} md={3}>
                                    <BaseButton
                                        text={t("homePage")}
                                        color="outline"
                                        radius="radius-10"
                                        size="medium"
                                        handleClick={handleNavigation.home}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
}

export default PaymentResult;
